import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputBase =
  "h-12 rounded-none border-line bg-white text-navy placeholder:text-slate-muted/70 focus-visible:ring-brand-green focus-visible:ring-2 md:text-base";

function FieldShell({ field, error, children }) {
  const full = field.width !== "half";
  return (
    <div className={full ? "sm:col-span-2" : "sm:col-span-1"}>
      <label
        htmlFor={field.name}
        className="mb-2 flex items-center justify-between text-sm font-medium text-navy"
      >
        <span>{field.label}</span>
        {field.optional && (
          <span className="text-xs font-normal text-slate-muted">Optional</span>
        )}
      </label>
      {children}
      {field.help && !error && (
        <p className="mt-1.5 text-xs text-slate-muted">{field.help}</p>
      )}
      {error && (
        <p role="alert" className="mt-1.5 text-xs font-medium text-red-600" data-testid={`error-${field.name}`}>
          {error}
        </p>
      )}
    </div>
  );
}

export default function FormBuilder({
  fields,
  schema,
  endpoint,
  submitLabel,
  success,
  defaultValues = {},
}) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { company: "", ...defaultValues },
  });
  const [done, setDone] = useState(false);
  // Stable per-form idempotency key: reused across retries/double-clicks so the
  // backend stores exactly one record and sends one set of emails.
  const [idempotencyKey] = useState(() =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );

  const onSubmit = async (values) => {
    try {
      await api.post(endpoint, { ...values, idempotency_key: idempotencyKey });
      setDone(true);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Something went wrong. Please try again."
      );
    }
  };

  if (done) {
    return (
      <div
        className="border border-line bg-cream p-8 sm:p-12"
        data-testid="form-success"
        role="status"
      >
        <div className="flex h-14 w-14 items-center justify-center bg-brand-green text-white">
          <Check className="h-7 w-7" />
        </div>
        <h2 className="mt-6 font-display text-2xl font-medium text-navy sm:text-3xl">
          {success.title}
        </h2>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-slate-body">
          {success.message}
        </p>
        <p className="mt-6 text-sm text-slate-muted">We aim to respond as soon as possible.</p>
        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-green hover:text-brand-greenDeep"
          data-testid="success-home-link"
        >
          Return to homepage
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2"
      noValidate
      data-testid="contact-form"
    >
      {fields.map((field) => {
        const error = errors[field.name]?.message;

        if (field.type === "textarea") {
          return (
            <FieldShell key={field.name} field={field} error={error}>
              <Textarea
                id={field.name}
                rows={field.rows || 5}
                placeholder={field.placeholder}
                className="rounded-none border-line bg-white text-navy placeholder:text-slate-muted/70 focus-visible:ring-2 focus-visible:ring-brand-green md:text-base"
                aria-invalid={!!error}
                data-testid={`field-${field.name}`}
                {...register(field.name)}
              />
            </FieldShell>
          );
        }

        if (field.type === "select") {
          return (
            <FieldShell key={field.name} field={field} error={error}>
              <Controller
                control={control}
                name={field.name}
                render={({ field: f }) => (
                  <Select value={f.value || ""} onValueChange={f.onChange}>
                    <SelectTrigger
                      id={field.name}
                      className={inputBase}
                      aria-invalid={!!error}
                      data-testid={`select-${field.name}`}
                    >
                      <SelectValue placeholder={field.placeholder || "Please select"} />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {field.options.map((opt) => (
                        <SelectItem key={opt} value={opt} data-testid={`option-${field.name}-${opt}`}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </FieldShell>
          );
        }

        if (field.type === "radio") {
          return (
            <FieldShell key={field.name} field={field} error={error}>
              <Controller
                control={control}
                name={field.name}
                render={({ field: f }) => (
                  <RadioGroup
                    value={f.value || ""}
                    onValueChange={f.onChange}
                    className="flex gap-6 pt-1"
                    data-testid={`radio-${field.name}`}
                  >
                    {field.options.map((opt) => (
                      <label key={opt} className="flex cursor-pointer items-center gap-2 text-sm text-navy">
                        <RadioGroupItem value={opt} data-testid={`radio-${field.name}-${opt}`} />
                        {opt}
                      </label>
                    ))}
                  </RadioGroup>
                )}
              />
            </FieldShell>
          );
        }

        if (field.type === "checkbox") {
          return (
            <div key={field.name} className="sm:col-span-2">
              <Controller
                control={control}
                name={field.name}
                render={({ field: f }) => (
                  <label className="flex cursor-pointer items-start gap-3">
                    <Checkbox
                      checked={!!f.value}
                      onCheckedChange={f.onChange}
                      className="mt-0.5 rounded-none border-navy data-[state=checked]:bg-brand-green data-[state=checked]:border-brand-green"
                      aria-invalid={!!error}
                      data-testid={`checkbox-${field.name}`}
                    />
                    <span className="text-sm leading-relaxed text-slate-body">
                      {field.labelNode || field.label}
                    </span>
                  </label>
                )}
              />
              {error && (
                <p role="alert" className="mt-1.5 text-xs font-medium text-red-600" data-testid={`error-${field.name}`}>
                  {error}
                </p>
              )}
            </div>
          );
        }

        // text / email / tel / date
        return (
          <FieldShell key={field.name} field={field} error={error}>
            <Input
              id={field.name}
              type={field.type || "text"}
              placeholder={field.placeholder}
              className={inputBase}
              aria-invalid={!!error}
              autoComplete={field.autoComplete}
              data-testid={`field-${field.name}`}
              {...register(field.name)}
            />
          </FieldShell>
        );
      })}

      {/* Honeypot — hidden from users, catches bots. */}
      <div className="hidden" aria-hidden="true">
        <label htmlFor="company">Company</label>
        <input
          id="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register("company")}
        />
      </div>

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex w-full items-center justify-center gap-2 bg-brand-green px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-brand-greenDeep disabled:opacity-60 sm:w-auto"
          data-testid="form-submit"
        >
          {isSubmitting ? "Sending…" : submitLabel}
          {!isSubmitting && <ArrowRight className="h-4 w-4" />}
        </button>
      </div>
    </form>
  );
}
